import React, { useState, FC, useRef, useEffect } from 'react';
import { Grid, TextField, Autocomplete } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import * as Yup from 'yup';

const useStyles = makeStyles(() =>
  createStyles({
    dropdownMain: {
      height: "32px"
    },
    selectField: {
      borderRadius: '5px',
      padding: "0px !important",
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          border: 'none',
          borderColor: '#8993A4',
        },
        '&:hover fieldset': {
          border: 'none',
          borderColor: '#8993A4',
        },
        '&.Mui-focused fieldset': {
          border: '2px solid',
          backgroundColor: 'transparent', // Make the background transparent when focused
        },
      },
      '& .MuiInputLabel-root': {
        textAlign: 'center',
        fontSize: '14px',
      },
    },
  })
);

interface Option {
  id: number;
  name: string;
  [key: string]: any;
}

interface Props {
  dropDownParms?: Option[];
  keyValue?: string[]; // Modified to accept an array of strings
  placeholder?: string;
  onChange?: (ids: number[] | number | null) => void;
  multiple?: boolean; // New prop to indicate if multiple selections are allowed
  useValidation?: boolean;
  ErrMultiple?:string;
  ErrSingle?:string;
  isClearSelection?:boolean | any;
  setIsClearSelection?:any
  selectedOption?:any
  isValidation?:boolean
  setClearValidation?:any
}

const SearchDropdown: FC<Props> = ({
  dropDownParms = [],
  keyValue = ['name'], // Default to first_name and last_name
  placeholder,
  onChange,
  multiple = false, // Default to single selection
  useValidation = false,
  ErrMultiple,
  ErrSingle,
  isClearSelection,
  setIsClearSelection,
  selectedOption,
  isValidation,
  
}) => {
  const [selectedFilters, setSelectedFilters] = useState<Option[] | Option | null>(selectedOption?selectedOption:multiple ? [] : null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const classes = useStyles();
  const autocompleteRef = useRef<HTMLDivElement>(null);

  // Yup validation schema
  const validationSchema = Yup.object().shape({
    selectedFilters: multiple
      ? Yup.array().min(1, ErrMultiple)
      : Yup.object().nullable().required(ErrSingle),
  });

  useEffect(() => {
    if (isValidation && useValidation) {
      validationSchema
        .validate({ selectedFilters })
        .then(() => setValidationError(null))
        .catch((err) => setValidationError(err.message));
    }
  }, [isValidation, selectedFilters, validationSchema,useValidation]);
  
  useEffect(() => {
    if (isClearSelection) {
      setSelectedFilters(null);
      setIsClearSelection(false)
    }
  }, [isClearSelection]);

  const handleChange = (event: any, newValue: Option[] | Option | null) => {
    setSelectedFilters(newValue);
    if (onChange) {
      if (multiple) {
        onChange(Array.isArray(newValue) ? newValue.map((option) => option.id) : []);
      } else {
        onChange(newValue ? (newValue as Option).id : null);
      }
    } else {
      console.warn('onChange is undefined');
    }

    validationSchema
      .validate({ selectedFilters: newValue })
      .then(() => setValidationError(null))
      .catch((err) => setValidationError(err.message));

      if (autocompleteRef.current) {
        const inputElement = autocompleteRef.current.querySelector('input');
        if (inputElement) {
          inputElement.blur();
        }
      }
  };

  const getOptionLabel = (option: Option) => {
    if (Array.isArray(keyValue)) {
      return keyValue.map(key => option[key]).join(' ');
    }
    return option[keyValue as string];
  };

  // Sort the options alphabetically by the first key in keyValue
  const sortedOptions = [...dropDownParms].sort((a, b) => {
    const aLabel = getOptionLabel(a).toLowerCase();
    const bLabel = getOptionLabel(b).toLowerCase();
    return aLabel.localeCompare(bLabel);
  });

  return (
    <Grid>
      <Grid>
        <Autocomplete
          ref={autocompleteRef}
          multiple={multiple}
          value={selectedFilters}
          onChange={handleChange}
          options={sortedOptions} // Use sorted options
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          sx={{ fontSize: "14px" }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder || "Select Option"}
              variant="outlined"
              className={classes.selectField}
              error={useValidation && !!validationError}
              helperText={useValidation && validationError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: `${useValidation && validationError ? "#ffcccc" : "#e9efee"}`,
                  padding: "0px 10px !important",
                  '&.Mui-focused': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: '#2b2b2b',
                },
                width: { lg: '100%', xs: '100%' },
                fontSize: "14px"
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default SearchDropdown;
